import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Pagination from '../../components/common/Pagination';
import { getApiBaseUrl, getFullImageUrl, getHeaders, getSchemaName, fetchETLStatus, fetchETLSyncHistory, triggerETLSync, fetchDBConnections, fetchLoginLogs } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import {
    ALARM_SOUND_IDS,
    getAlarmSoundId,
    setAlarmSoundId,
    isAlarmMuted,
    setAlarmMuted,
    previewAlarmSound,
} from '../../utils/alarmSound';
import ManagementHeader from '../management/ManagementHeader';
import SystemTabs, { SYSTEM_TABS } from './SystemTabs';
import '../management/management.css';
import './SystemManagementPage.css';

const DASH = '—';

function SystemManagementPage() {
    const location = useLocation();
    const { t } = useTranslation();
    const [activeMenu, setActiveMenu] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('tab') || 'general';
    });
    const [loading, setLoading] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [saveMessageError, setSaveMessageError] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingFavicon, setUploadingFavicon] = useState(false);

    // 알람 소리 설정 (브라우저 로컬 저장)
    const [alarmSoundId, setAlarmSoundIdState] = useState(() => getAlarmSoundId());
    const [alarmMuted, setAlarmMutedState] = useState(() => isAlarmMuted());

    const handleAlarmSoundChange = (id) => {
        setAlarmSoundIdState(id);
        setAlarmSoundId(id);
        if (!alarmMuted) previewAlarmSound(id);
    };

    const handleAlarmMuteToggle = (muted) => {
        setAlarmMutedState(muted);
        setAlarmMuted(muted);
    };

    // ETL 상태 관련 state
    const [etlStatus, setEtlStatus] = useState(null);
    const [etlHistory, setEtlHistory] = useState([]);
    const [dbConnections, setDbConnections] = useState(null);
    const [etlLoading, setEtlLoading] = useState(false);
    const [syncTriggering, setSyncTriggering] = useState(false);

    // 로그인 로그 관련 state
    const [loginLogs, setLoginLogs] = useState([]);
    const [loginLogsLoading, setLoginLogsLoading] = useState(false);
    const [logFilter, setLogFilter] = useState(''); // '', 'LOGIN', 'LOGOUT', 'LOGIN_FAILED'

    // ETL 페이지네이션 상태
    const [etlPage, setEtlPage] = useState(0);
    const [etlSize, setEtlSize] = useState(10);

    // 로그 페이지네이션 상태
    const [logPage, setLogPage] = useState(0);
    const [logSize, setLogSize] = useState(10);

    // URL 파라미터에서 탭 정보 가져오기
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab) {
            setActiveMenu(tab);
        }
    }, [location.search]);

    const [formData, setFormData] = useState({
        systemName: '',
        companyName: '',
        companyCode: '',
        schemaName: '',
        businessNumber: '',
        representativeName: '',
        contactEmail: '',
        contactPhone: '',
        address: '',
        logoUrl: '',
        faviconUrl: '',
        refreshInterval: '30초',
        timezone: 'Asia/Seoul',
        // 보안 설정
        maxLoginAttempts: 5,
        lockDurationMinutes: 30,
        // 알림 설정
        notifyWeb: true,
        notifyEmail: false,
        notifySms: false,
        notifyPush: false
    });

    // 시스템 설정 조회
    useEffect(() => {
        const fetchSystemSettings = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${getApiBaseUrl()}/system/settings`, {
                    method: 'GET',
                    headers: getHeaders()
                });
                const result = await response.json();
                if (result.success) {
                    const data = result.data;
                    setFormData({
                        systemName: data.systemName || t('pages.system.management.common.defaultSystemName'),
                        companyName: data.companyName || '',
                        companyCode: data.companyCode || '',
                        schemaName: data.schemaName || '',
                        businessNumber: data.businessNumber || '',
                        representativeName: data.representativeName || '',
                        contactEmail: data.contactEmail || '',
                        contactPhone: data.contactPhone || '',
                        address: data.address || '',
                        logoUrl: data.logoUrl || '',
                        faviconUrl: data.faviconUrl || '',
                        refreshInterval: data.refreshInterval || '30초',
                        timezone: data.timezone || 'Asia/Seoul',
                        // 보안 설정
                        maxLoginAttempts: data.maxLoginAttempts || 5,
                        lockDurationMinutes: data.lockDurationMinutes || 30,
                        // 알림 설정
                        notifyWeb: data.notifyWeb !== undefined ? data.notifyWeb : true,
                        notifyEmail: data.notifyEmail !== undefined ? data.notifyEmail : false,
                        notifySms: data.notifySms !== undefined ? data.notifySms : false,
                        notifyPush: data.notifyPush !== undefined ? data.notifyPush : false
                    });
                }
            } catch (error) {
                console.error('시스템 설정 조회 실패:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSystemSettings();
    }, []);

    // ETL 데이터 로드 함수
    const loadETLData = useCallback(async () => {
        setEtlLoading(true);
        try {
            const [statusResult, historyResult, dbResult] = await Promise.allSettled([
                fetchETLStatus(),
                fetchETLSyncHistory(100),
                fetchDBConnections()
            ]);

            if (statusResult.status === 'fulfilled') {
                setEtlStatus(statusResult.value);
            }
            if (historyResult.status === 'fulfilled') {
                setEtlHistory(historyResult.value || []);
            }
            if (dbResult.status === 'fulfilled') {
                setDbConnections(dbResult.value);
            }
        } catch (error) {
            console.error('ETL 데이터 로드 실패:', error);
        } finally {
            setEtlLoading(false);
        }
    }, []);

    // 데이터 수집관리 탭 선택시 ETL 데이터 로드
    useEffect(() => {
        if (activeMenu === 'data-collection') {
            loadETLData();
        }
    }, [activeMenu, loadETLData]);

    // ETL 수동 실행 핸들러
    const handleTriggerSync = async () => {
        setSyncTriggering(true);
        try {
            await triggerETLSync();
            setSaveMessage(t('pages.system.management.dataCollection.messages.syncStarted'));
            setSaveMessageError(false);
            setTimeout(() => setSaveMessage(''), 3000);
            // 3초 후 상태 새로고침
            setTimeout(() => loadETLData(), 3000);
        } catch (error) {
            console.error('ETL 동기화 실행 실패:', error);
            setSaveMessage(t('pages.system.management.dataCollection.messages.syncFailed'));
            setSaveMessageError(true);
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setSyncTriggering(false);
        }
    };

    // 로그인 로그 로드 함수
    const loadLoginLogs = useCallback(async (eventType = '') => {
        setLoginLogsLoading(true);
        try {
            const logs = await fetchLoginLogs(0, 50, eventType || null);
            setLoginLogs(logs || []);
        } catch (error) {
            console.error('로그인 로그 로드 실패:', error);
            setLoginLogs([]);
        } finally {
            setLoginLogsLoading(false);
        }
    }, []);

    // 시스템로그 탭 선택시 로그인 로그 로드
    useEffect(() => {
        if (activeMenu === 'log') {
            loadLoginLogs(logFilter);
        }
    }, [activeMenu, logFilter, loadLoginLogs]);

    // 로그 필터 변경 핸들러
    const handleLogFilterChange = (filter) => {
        setLogFilter(filter);
        setLogPage(0);
    };

    // ETL 페이지네이션 적용
    const paginatedEtlHistory = useMemo(() => {
        const startIndex = etlPage * etlSize;
        return etlHistory.slice(startIndex, startIndex + etlSize);
    }, [etlHistory, etlPage, etlSize]);

    const etlTotalElements = etlHistory.length;
    const etlTotalPages = Math.ceil(etlTotalElements / etlSize);

    const handleEtlPageChange = (newPage) => {
        setEtlPage(newPage);
    };

    const handleEtlSizeChange = (newSize) => {
        setEtlSize(newSize);
        setEtlPage(0);
    };

    // 로그 페이지네이션 적용
    const paginatedLogs = useMemo(() => {
        const startIndex = logPage * logSize;
        return loginLogs.slice(startIndex, startIndex + logSize);
    }, [loginLogs, logPage, logSize]);

    const logTotalElements = loginLogs.length;
    const logTotalPages = Math.ceil(logTotalElements / logSize);

    const handleLogPageChange = (newPage) => {
        setLogPage(newPage);
    };

    const handleLogSizeChange = (newSize) => {
        setLogSize(newSize);
        setLogPage(0);
    };

    // 이벤트 타입 한글 변환
    const getEventTypeLabel = (eventType) => {
        switch (eventType) {
            case 'LOGIN': return t('pages.system.management.log.filters.login');
            case 'LOGOUT': return t('pages.system.management.log.filters.logout');
            case 'LOGIN_FAILED': return t('pages.system.management.log.filters.loginFailed');
            default: return eventType;
        }
    };

    // 이벤트 타입별 스타일 클래스
    const getEventTypeClass = (eventType) => {
        switch (eventType) {
            case 'LOGIN': return 'success';
            case 'LOGOUT': return 'info';
            case 'LOGIN_FAILED': return 'error';
            default: return '';
        }
    };

    // 시간 포맷팅 함수
    const formatDateTime = (dateStr) => {
        if (!dateStr) return DASH;
        const date = new Date(dateStr);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    /* DB 연결 상태: 응답이 없으면 연결 실패로 단정하지 않고 확인 불가로 둔다 */
    const dbState = (key) => {
        const connected = dbConnections?.databases?.[key]?.connected;
        if (connected == null) return 'unknown';
        return connected ? 'connected' : 'disconnected';
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 알림 토글 변경 핸들러 (즉시 저장)
    const handleNotifyToggle = async (field, value) => {
        // UI 먼저 업데이트
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // 서버에 즉시 저장
        try {
            const response = await fetch(`${getApiBaseUrl()}/system/settings`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({
                    [field]: value
                })
            });
            const result = await response.json();
            if (!result.success) {
                // 실패 시 롤백
                setFormData(prev => ({
                    ...prev,
                    [field]: !value
                }));
            }
        } catch (error) {
            console.error('알림 설정 저장 실패:', error);
            // 실패 시 롤백
            setFormData(prev => ({
                ...prev,
                [field]: !value
            }));
        }
    };

    // 파일 업로드 핸들러
    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
        setUploading(true);

        const fieldName = type === 'logo' ? 'logoUrl' : 'faviconUrl';
        const previewFieldName = `${fieldName}Preview`;
        const oldUrl = formData[fieldName]; // 기존 파일 URL 저장

        // 로컬 미리보기를 위해 FileReader 사용 (base64)
        const base64Promise = new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                resolve(event.target.result);
            };
            reader.readAsDataURL(file);
        });

        try {
            // base64 미리보기 먼저 설정
            const base64Data = await base64Promise;
            setFormData(prev => ({
                ...prev,
                [previewFieldName]: base64Data
            }));

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const schemaName = getSchemaName();

            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            formDataUpload.append('type', type);

            const response = await fetch(`${getApiBaseUrl()}/upload/image`, {
                method: 'POST',
                headers: {
                    'X-Tenant-ID': schemaName,
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: formDataUpload
            });

            const result = await response.json();
            if (result.success) {
                const pendingDeleteField = `${fieldName}PendingDelete`;

                // 업로드 성공 - 서버 URL 저장, 기존 파일은 삭제 예정으로 표시
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: result.url,
                    [previewFieldName]: base64Data, // base64 미리보기 유지
                    // 기존 파일이 있으면 삭제 예정으로 저장 (저장 시 실제 삭제)
                    [pendingDeleteField]: oldUrl && oldUrl.startsWith('/uploads/') ? oldUrl : prev[pendingDeleteField]
                }));
                setSaveMessage(type === 'logo' ? t('pages.system.management.general.branding.logoUploadComplete') : t('pages.system.management.general.branding.faviconUploadComplete'));
                setSaveMessageError(false);
                setTimeout(() => setSaveMessage(''), 3000);
            } else {
                // 실패 시 미리보기 제거
                setFormData(prev => ({
                    ...prev,
                    [previewFieldName]: ''
                }));
                setSaveMessage(result.error || t('pages.system.management.general.branding.uploadFailed'));
                setSaveMessageError(true);
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('파일 업로드 실패:', error);
            setFormData(prev => ({
                ...prev,
                [previewFieldName]: ''
            }));
            setSaveMessage(t('pages.system.management.general.branding.fileUploadFailed'));
            setSaveMessageError(true);
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setUploading(false);
            e.target.value = ''; // 같은 파일 다시 선택 가능하도록
        }
    };

    // 파일 삭제 핸들러 (UI에서만 삭제 표시, 실제 삭제는 저장 시)
    const handleFileDelete = (type) => {
        const fieldName = type === 'logo' ? 'logoUrl' : 'faviconUrl';
        const previewFieldName = `${fieldName}Preview`;
        const pendingDeleteField = `${fieldName}PendingDelete`;

        // 삭제 예정으로 표시하고 UI에서 숨김
        setFormData(prev => ({
            ...prev,
            [pendingDeleteField]: prev[fieldName], // 삭제 예정 URL 저장
            [fieldName]: '',
            [previewFieldName]: ''
        }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setSaveMessage('');

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const schemaName = getSchemaName();

            // 삭제 예정 파일들 실제 삭제
            const deletePromises = [];
            if (formData.logoUrlPendingDelete && formData.logoUrlPendingDelete.startsWith('/uploads/')) {
                deletePromises.push(
                    fetch(`${getApiBaseUrl()}/upload/image?url=${encodeURIComponent(formData.logoUrlPendingDelete)}`, {
                        method: 'DELETE',
                        headers: {
                            'X-Tenant-ID': schemaName,
                            ...(token && { 'Authorization': `Bearer ${token}` })
                        }
                    }).catch(err => console.warn('로고 파일 삭제 실패:', err))
                );
            }
            if (formData.faviconUrlPendingDelete && formData.faviconUrlPendingDelete.startsWith('/uploads/')) {
                deletePromises.push(
                    fetch(`${getApiBaseUrl()}/upload/image?url=${encodeURIComponent(formData.faviconUrlPendingDelete)}`, {
                        method: 'DELETE',
                        headers: {
                            'X-Tenant-ID': schemaName,
                            ...(token && { 'Authorization': `Bearer ${token}` })
                        }
                    }).catch(err => console.warn('파비콘 파일 삭제 실패:', err))
                );
            }

            // 파일 삭제와 설정 저장 병렬 처리
            const [, response] = await Promise.all([
                Promise.all(deletePromises),
                fetch(`${getApiBaseUrl()}/system/settings`, {
                    method: 'PUT',
                    headers: getHeaders(),
                    body: JSON.stringify({
                        companyName: formData.companyName,
                        contactEmail: formData.contactEmail,
                        contactPhone: formData.contactPhone,
                        address: formData.address,
                        logoUrl: formData.logoUrl,
                        faviconUrl: formData.faviconUrl,
                        refreshInterval: formData.refreshInterval,
                        timezone: formData.timezone,
                        // 보안 설정
                        maxLoginAttempts: formData.maxLoginAttempts,
                        lockDurationMinutes: formData.lockDurationMinutes,
                        // 알림 설정
                        notifyWeb: formData.notifyWeb,
                        notifyEmail: formData.notifyEmail,
                        notifySms: formData.notifySms,
                        notifyPush: formData.notifyPush
                    })
                })
            ]);

            const result = await response.json();
            if (result.success) {
                // 삭제 예정 상태 초기화
                setFormData(prev => ({
                    ...prev,
                    logoUrlPendingDelete: '',
                    faviconUrlPendingDelete: ''
                }));
                setSaveMessage(t('pages.system.management.common.saveSuccess'));
                setSaveMessageError(false);
                // 잠시 후 페이지 새로고침
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                setSaveMessage(t('pages.system.management.common.saveFailed'));
                setSaveMessageError(true);
                setTimeout(() => setSaveMessage(''), 3000);
            }
        } catch (error) {
            console.error('설정 저장 실패:', error);
            setSaveMessage(t('pages.system.management.common.saveFailed'));
            setSaveMessageError(true);
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        switch (activeMenu) {
            case 'general':
                return (
                    <div className="system-settings-content">
                        <div className="settings-form">
                            {/* 기본 정보 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.general.sectionBasic')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.systemName')}</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.systemName}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.companyName')}</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.companyName}
                                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.companyCode')}</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.companyCode}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.schemaName')}</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.schemaName}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.businessNumber')}</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.businessNumber}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.representativeName')}</label>
                                        <input
                                            type="text"
                                            className="form-input readonly"
                                            value={formData.representativeName}
                                            readOnly
                                            disabled
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 연락처 정보 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.general.sectionContact')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.contactEmail')}</label>
                                        <input
                                            type="email"
                                            className="form-input"
                                            value={formData.contactEmail}
                                            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                                            placeholder={t('pages.system.management.general.placeholders.contactEmail')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.contactPhone')}</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.contactPhone}
                                            onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                                            placeholder={t('pages.system.management.general.placeholders.contactPhone')}
                                        />
                                    </div>
                                </div>
                                <div className="form-row single">
                                    <div className="form-group full-width">
                                        <label className="form-label">{t('pages.system.management.general.labels.address')}</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.address}
                                            onChange={(e) => handleInputChange('address', e.target.value)}
                                            placeholder={t('pages.system.management.general.placeholders.address')}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 브랜딩 정보 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.general.sectionBranding')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.logo')}</label>
                                        <div className="file-upload-container">
                                            {(formData.logoUrlPreview || formData.logoUrl) && (
                                                <div className="image-preview">
                                                    <img src={formData.logoUrlPreview || getFullImageUrl(formData.logoUrl)} alt={t('pages.system.management.general.branding.logoAlt')} />
                                                </div>
                                            )}
                                            <div className="file-upload-actions">
                                                <input
                                                    type="file"
                                                    id="logo-upload"
                                                    accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileUpload(e, 'logo')}
                                                />
                                                <label htmlFor="logo-upload" className="file-upload-btn">
                                                    <span className="material-symbols-outlined">upload</span>
                                                    {formData.logoUrl ? t('pages.system.management.general.branding.change') : t('pages.system.management.general.branding.upload')}
                                                </label>
                                                {formData.logoUrl && (
                                                    <button
                                                        type="button"
                                                        className="file-delete-btn"
                                                        onClick={() => handleFileDelete('logo')}
                                                    >
                                                        <span className="material-symbols-outlined">delete</span>
                                                        {t('pages.system.management.general.branding.delete')}
                                                    </button>
                                                )}
                                            </div>
                                            {uploadingLogo && <span className="upload-status">{t('pages.system.management.general.branding.uploading')}</span>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.general.labels.favicon')}</label>
                                        <div className="file-upload-container">
                                            {(formData.faviconUrlPreview || formData.faviconUrl) && (
                                                <div className="image-preview favicon-preview">
                                                    <img src={formData.faviconUrlPreview || getFullImageUrl(formData.faviconUrl)} alt={t('pages.system.management.general.branding.faviconAlt')} />
                                                </div>
                                            )}
                                            <div className="file-upload-actions">
                                                <input
                                                    type="file"
                                                    id="favicon-upload"
                                                    accept="image/png,image/jpeg,image/jpg,image/gif,image/ico,image/x-icon,image/svg+xml"
                                                    style={{ display: 'none' }}
                                                    onChange={(e) => handleFileUpload(e, 'favicon')}
                                                />
                                                <label htmlFor="favicon-upload" className="file-upload-btn">
                                                    <span className="material-symbols-outlined">upload</span>
                                                    {formData.faviconUrl ? t('pages.system.management.general.branding.change') : t('pages.system.management.general.branding.upload')}
                                                </label>
                                                {formData.faviconUrl && (
                                                    <button
                                                        type="button"
                                                        className="file-delete-btn"
                                                        onClick={() => handleFileDelete('favicon')}
                                                    >
                                                        <span className="material-symbols-outlined">delete</span>
                                                        {t('pages.system.management.general.branding.delete')}
                                                    </button>
                                                )}
                                            </div>
                                            {uploadingFavicon && <span className="upload-status">{t('pages.system.management.general.branding.uploading')}</span>}
                                        </div>
                                    </div>
                                </div>
                                <p className="form-hint">{t('pages.system.management.general.branding.hint')}</p>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessageError ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? t('pages.system.management.common.saving') : t('pages.system.management.common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'monitoring':
                return (
                    <div className="system-settings-content">
                        <div className="settings-form">
                            {/* 대시보드 설정 섹션 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.monitoring.sectionDashboard')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.monitoring.labels.refreshInterval')}</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.refreshInterval || '30초'}
                                                onChange={(e) => handleInputChange('refreshInterval', e.target.value)}
                                            >
                                                <option value="수집완료시">{t('pages.system.management.monitoring.refreshOptions.onComplete')}</option>
                                                <option value="10초">{t('pages.system.management.monitoring.refreshOptions.sec10')}</option>
                                                <option value="30초">{t('pages.system.management.monitoring.refreshOptions.sec30')}</option>
                                                <option value="1분">{t('pages.system.management.monitoring.refreshOptions.min1')}</option>
                                                <option value="5분">{t('pages.system.management.monitoring.refreshOptions.min5')}</option>
                                                <option value="10분">{t('pages.system.management.monitoring.refreshOptions.min10')}</option>
                                                <option value="사용안함">{t('pages.system.management.monitoring.refreshOptions.disabled')}</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.monitoring.labels.timezone')}</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.timezone || 'Asia/Seoul'}
                                                onChange={(e) => handleInputChange('timezone', e.target.value)}
                                            >
                                                <option value="Asia/Seoul">{t('pages.system.management.monitoring.timezones.seoul')}</option>
                                                <option value="Asia/Ho_Chi_Minh">{t('pages.system.management.monitoring.timezones.hochiminh')}</option>
                                                <option value="UTC">{t('pages.system.management.monitoring.timezones.utc')}</option>
                                                <option value="Asia/Tokyo">{t('pages.system.management.monitoring.timezones.tokyo')}</option>
                                                <option value="America/New_York">{t('pages.system.management.monitoring.timezones.newyork')}</option>
                                                <option value="Europe/London">{t('pages.system.management.monitoring.timezones.london')}</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessageError ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? t('pages.system.management.common.saving') : t('pages.system.management.common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'alarm':
                return (
                    <div className="system-settings-content">
                        <div className="settings-form">
                            {/* 알림 채널 설정 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.alarm.sectionChannels')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.alarm.labels.web')}</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifyWeb}
                                                    onChange={(e) => handleNotifyToggle('notifyWeb', e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifyWeb ? t('pages.system.management.alarm.on') : t('pages.system.management.alarm.off')}</span>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.alarm.hints.web')}</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.alarm.labels.email')}</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch disabled">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifyEmail}
                                                    disabled
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifyEmail ? t('pages.system.management.alarm.on') : t('pages.system.management.alarm.off')}</span>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.alarm.hints.email')}</span>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.alarm.labels.sms')}</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch disabled">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifySms}
                                                    disabled
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifySms ? t('pages.system.management.alarm.on') : t('pages.system.management.alarm.off')}</span>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.alarm.hints.sms')}</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.alarm.labels.push')}</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch disabled">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.notifyPush}
                                                    disabled
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{formData.notifyPush ? t('pages.system.management.alarm.on') : t('pages.system.management.alarm.off')}</span>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.alarm.hints.push')}</span>
                                    </div>
                                </div>
                            </div>

                            {/* 알람 소리 설정 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.alarm.sectionSound')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.alarm.labels.sound')}</label>
                                        <div className="alarm-sound-row">
                                            <select
                                                className="form-select"
                                                value={alarmSoundId}
                                                onChange={(e) => handleAlarmSoundChange(e.target.value)}
                                                disabled={alarmMuted}
                                            >
                                                {ALARM_SOUND_IDS.map((id) => (
                                                    <option key={id} value={id}>
                                                        {t(`pages.system.management.alarm.sounds.${id}`)}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="alarm-sound-preview-btn"
                                                onClick={() => previewAlarmSound(alarmSoundId)}
                                                disabled={alarmMuted}
                                            >
                                                <span className="material-symbols-outlined">play_arrow</span>
                                                {t('pages.system.management.alarm.preview')}
                                            </button>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.alarm.hints.sound')}</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.alarm.labels.mute')}</label>
                                        <div className="toggle-container">
                                            <label className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={alarmMuted}
                                                    onChange={(e) => handleAlarmMuteToggle(e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-status">{alarmMuted ? t('pages.system.management.alarm.on') : t('pages.system.management.alarm.off')}</span>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.alarm.hints.mute')}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessageError ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? t('pages.system.management.common.saving') : t('pages.system.management.common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'security':
                return (
                    <div className="system-settings-content">
                        <div className="settings-form">
                            {/* 로그인 보안 설정 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.security.sectionLogin')}</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.security.labels.maxAttempts')}</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.maxLoginAttempts}
                                                onChange={(e) => handleInputChange('maxLoginAttempts', parseInt(e.target.value))}
                                            >
                                                <option value={3}>{t('pages.system.management.security.attempts.n3')}</option>
                                                <option value={5}>{t('pages.system.management.security.attempts.n5')}</option>
                                                <option value={10}>{t('pages.system.management.security.attempts.n10')}</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.security.hints.maxAttempts')}</span>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('pages.system.management.security.labels.lockDuration')}</label>
                                        <div className="select-container">
                                            <select
                                                className="form-select"
                                                value={formData.lockDurationMinutes}
                                                onChange={(e) => handleInputChange('lockDurationMinutes', parseInt(e.target.value))}
                                            >
                                                <option value={5}>{t('pages.system.management.security.duration.m5')}</option>
                                                <option value={10}>{t('pages.system.management.security.duration.m10')}</option>
                                                <option value={30}>{t('pages.system.management.security.duration.m30')}</option>
                                                <option value={60}>{t('pages.system.management.security.duration.h1')}</option>
                                                <option value={1440}>{t('pages.system.management.security.duration.h24')}</option>
                                            </select>
                                            <svg className="select-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <span className="form-hint">{t('pages.system.management.security.hints.lockDuration')}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessageError ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button className="save-btn" onClick={handleSave} disabled={loading}>
                                    {loading ? t('pages.system.management.common.saving') : t('pages.system.management.common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'data-collection':
                return (
                    <div className="system-settings-content">
                        <div className="settings-form">
                            {/* ETL 현재 상태 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.dataCollection.sectionStatus')}</h3>
                                {etlLoading ? (
                                    <p className="coming-soon">{t('pages.system.management.common.loading')}</p>
                                ) : (
                                    <div className="etl-status-grid">
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className={`material-symbols-outlined ${etlStatus == null ? '' : etlStatus.is_running ? 'status-running' : 'status-idle'}`}>
                                                    {etlStatus == null ? 'help' : etlStatus.is_running ? 'sync' : 'check_circle'}
                                                </span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">{t('pages.system.management.dataCollection.cards.runState')}</span>
                                                <span className={`etl-status-value ${etlStatus == null ? '' : etlStatus.is_running ? 'running' : 'idle'}`}>
                                                    {etlStatus == null ? DASH : etlStatus.is_running ? t('pages.system.management.dataCollection.runState.running') : t('pages.system.management.dataCollection.runState.idle')}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className="material-symbols-outlined">schedule</span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">{t('pages.system.management.dataCollection.cards.lastRun')}</span>
                                                <span className="etl-status-value">{formatDateTime(etlStatus?.last_run)}</span>
                                            </div>
                                        </div>
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className={`material-symbols-outlined ${etlStatus?.last_success ? 'status-success' : ''}`}>verified</span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">{t('pages.system.management.dataCollection.cards.lastSuccess')}</span>
                                                <span className="etl-status-value">{formatDateTime(etlStatus?.last_success)}</span>
                                            </div>
                                        </div>
                                        <div className="etl-status-card">
                                            <div className="etl-status-icon">
                                                <span className="material-symbols-outlined">database</span>
                                            </div>
                                            <div className="etl-status-info">
                                                <span className="etl-status-label">{t('pages.system.management.dataCollection.cards.recordsProcessed')}</span>
                                                <span className="etl-status-value">{etlStatus?.records_processed == null ? DASH : t('pages.system.management.dataCollection.recordsUnit', { n: etlStatus.records_processed.toLocaleString() })}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* DB 연결 상태 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.dataCollection.sectionDb')}</h3>
                                <div className="db-connection-grid">
                                    <div className={`db-connection-card ${dbState('raw_db')}`}>
                                        <div className="db-icon">
                                            <span className="material-symbols-outlined">
                                                {{ connected: 'cloud_done', disconnected: 'cloud_off', unknown: 'help' }[dbState('raw_db')]}
                                            </span>
                                        </div>
                                        <div className="db-info">
                                            <span className="db-name">{t('pages.system.management.dataCollection.db.rawName')}</span>
                                            <span className="db-status">
                                                {t(`pages.system.management.dataCollection.db.${dbState('raw_db')}`)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`db-connection-card ${dbState('monitoring_db')}`}>
                                        <div className="db-icon">
                                            <span className="material-symbols-outlined">
                                                {{ connected: 'cloud_done', disconnected: 'cloud_off', unknown: 'help' }[dbState('monitoring_db')]}
                                            </span>
                                        </div>
                                        <div className="db-info">
                                            <span className="db-name">{t('pages.system.management.dataCollection.db.monitoringName')}</span>
                                            <span className="db-status">
                                                {t(`pages.system.management.dataCollection.db.${dbState('monitoring_db')}`)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ETL 동기화 이력 */}
                            <div className="form-section no-padding-bottom">
                                <h3 className="form-section-title">{t('pages.system.management.dataCollection.sectionHistory')}</h3>
                                <div className="etl-history-table-container">
                                    <table className="etl-history-table">
                                        <thead>
                                            <tr>
                                                <th>{t('pages.system.management.dataCollection.history.col.time')}</th>
                                                <th>{t('pages.system.management.dataCollection.history.col.status')}</th>
                                                <th>{t('pages.system.management.dataCollection.history.col.records')}</th>
                                                <th>{t('pages.system.management.dataCollection.history.col.tables')}</th>
                                                <th>{t('pages.system.management.dataCollection.history.col.note')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedEtlHistory.length > 0 ? (
                                                paginatedEtlHistory.map((item, index) => (
                                                    <tr key={item.id || index}>
                                                        <td>{formatDateTime(item.sync_time)}</td>
                                                        <td>
                                                            <span className={`status-badge ${item.sync_status?.toLowerCase()}`}>
                                                                {item.sync_status === 'SUCCESS' ? t('pages.system.management.dataCollection.history.statusSuccess') : t('pages.system.management.dataCollection.history.statusFailed')}
                                                            </span>
                                                        </td>
                                                        <td>{t('pages.system.management.dataCollection.recordsUnit', { n: item.total_records?.toLocaleString() || 0 })}</td>
                                                        <td className="table-list">{item.table_list || DASH}</td>
                                                        <td className="error-message">{item.error_message || DASH}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="no-data">{t('pages.system.management.dataCollection.history.empty')}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {etlTotalElements > 0 && (
                                    <Pagination
                                        page={etlPage}
                                        totalPages={etlTotalPages}
                                        totalElements={etlTotalElements}
                                        size={etlSize}
                                        onPageChange={handleEtlPageChange}
                                        onSizeChange={handleEtlSizeChange}
                                    />
                                )}
                            </div>

                            <div className="form-actions">
                                {saveMessage && (
                                    <span className={`save-message ${saveMessageError ? 'error' : 'success'}`}>
                                        {saveMessage}
                                    </span>
                                )}
                                <button
                                    className="save-btn secondary"
                                    onClick={loadETLData}
                                    disabled={etlLoading}
                                >
                                    <span className="material-symbols-outlined">refresh</span>
                                    {t('pages.system.management.dataCollection.actions.refresh')}
                                </button>
                                <button
                                    className="save-btn"
                                    onClick={handleTriggerSync}
                                    disabled={syncTriggering}
                                >
                                    <span className="material-symbols-outlined">sync</span>
                                    {syncTriggering ? t('pages.system.management.dataCollection.actions.running') : etlStatus?.is_running ? t('pages.system.management.dataCollection.actions.inProgress') : t('pages.system.management.dataCollection.actions.manualSync')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            case 'log':
                return (
                    <div className="system-settings-content">
                        <div className="settings-form">
                            {/* 필터 버튼 */}
                            <div className="form-section">
                                <h3 className="form-section-title">{t('pages.system.management.log.sectionHistory')}</h3>
                                <div className="log-filter-buttons">
                                    <button
                                        className={`filter-btn ${logFilter === '' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('')}
                                    >
                                        {t('pages.system.management.log.filters.all')}
                                    </button>
                                    <button
                                        className={`filter-btn ${logFilter === 'LOGIN' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('LOGIN')}
                                    >
                                        {t('pages.system.management.log.filters.login')}
                                    </button>
                                    <button
                                        className={`filter-btn ${logFilter === 'LOGOUT' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('LOGOUT')}
                                    >
                                        {t('pages.system.management.log.filters.logout')}
                                    </button>
                                    <button
                                        className={`filter-btn ${logFilter === 'LOGIN_FAILED' ? 'active' : ''}`}
                                        onClick={() => handleLogFilterChange('LOGIN_FAILED')}
                                    >
                                        {t('pages.system.management.log.filters.loginFailed')}
                                    </button>
                                </div>
                            </div>

                            {/* 로그 테이블 */}
                            <div className="form-section no-padding-bottom">
                                <div className="login-log-table-wrapper">
                                    {loginLogsLoading ? (
                                        <p className="coming-soon">{t('pages.system.management.common.loading')}</p>
                                    ) : (
                                        <table className="etl-history-table login-log-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('pages.system.management.log.col.time')}</th>
                                                    <th>{t('pages.system.management.log.col.type')}</th>
                                                    <th>{t('pages.system.management.log.col.user')}</th>
                                                    <th>{t('pages.system.management.log.col.email')}</th>
                                                    <th>{t('pages.system.management.log.col.ip')}</th>
                                                    <th>{t('pages.system.management.log.col.note')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedLogs.length > 0 ? (
                                                    paginatedLogs.map((log, index) => (
                                                        <tr key={log.id || index}>
                                                            <td>{formatDateTime(log.createdAt)}</td>
                                                            <td>
                                                                <span className={`status-badge ${getEventTypeClass(log.eventType)}`}>
                                                                    {getEventTypeLabel(log.eventType)}
                                                                </span>
                                                            </td>
                                                            <td>{log.username || DASH}</td>
                                                            <td>{log.email || DASH}</td>
                                                            <td>{log.ipAddress || DASH}</td>
                                                            <td className="error-message">
                                                                {log.eventType === 'LOGIN_FAILED' ? (log.failureReason || DASH) : DASH}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="no-data">{t('pages.system.management.log.empty')}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {logTotalElements > 0 && (
                                    <Pagination
                                        page={logPage}
                                        totalPages={logTotalPages}
                                        totalElements={logTotalElements}
                                        size={logSize}
                                        onPageChange={handleLogPageChange}
                                        onSizeChange={handleLogSizeChange}
                                    />
                                )}
                            </div>

                            <div className="form-actions">
                                <button
                                    className="save-btn secondary"
                                    onClick={() => loadLoginLogs(logFilter)}
                                    disabled={loginLogsLoading}
                                >
                                    <span className="material-symbols-outlined">refresh</span>
                                    {t('pages.system.management.common.refresh')}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <MainLayout>
            <div className="mg-page system-management-container">
                <ManagementHeader
                    rootLabel={t('sidebar.system.title')}
                    icon="settings"
                    crumbs={[{ label: t((SYSTEM_TABS.find((tab) => tab.id === activeMenu) || SYSTEM_TABS[0]).labelKey) }]}
                />
                <SystemTabs active={activeMenu} />
                {renderContent()}
            </div>
        </MainLayout>
    );
}

export default SystemManagementPage;

